import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useMemo } from "react";
import {
  ChartCard,
  ChartEmptyState,
  ChartErrorState,
  ChartLoading,
  MultiLineChart,
  type MultiLineChartConfig,
} from "@/shared/charts";
import { ThroughputIcon } from "@/shared/ui/console-icons";
import type { ECharts } from "echarts";
import { ThroughputLineSelector } from "@/features/deployment-dashboard/widgets/charts/throughput-trend/throughput-line-selector";
import type { AreaLocation } from "@/features/deployment-dashboard/types";
import type {
  ThroughputLineTrendData,
  ThroughputLineOption,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/types";
import {
  getThroughputTrendErrorMessage,
  mapLineTrendDataToChartSeries,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend/throughput-trend-chart.utils";
import { ThroughputLineMap } from "@/features/deployment-dashboard/widgets/panels/throughput-line-map";

interface ThroughputTrendChartProps {
  /** Trend data for each currently selected line. */
  lineTrendData: ThroughputLineTrendData[];
  /**
   * Trend data for every configured line, regardless of selection. Used to populate the side
   * panel's In/Out/Net totals for all lines as soon as the page loads. Defaults to
   * `lineTrendData` when not provided.
   */
  allLineTrendData?: ThroughputLineTrendData[];
  /** True while any selected line's data is loading. */
  loading?: boolean;
  /** First error from any selected line's data fetch. */
  error?: Error | string | null;
  /** All available throughput lines (for the side panel). */
  lines: ThroughputLineOption[];
  linesLoading?: boolean;
  linesError?: Error | string | null;
  selectedLineKeys: ReadonlySet<string>;
  onLineKeysChange: (keys: ReadonlySet<string>) => void;
  onLineHover?: (key: string | null) => void;
  deploymentId?: string;
  areas?: AreaLocation[];
  hoveredLineKey?: string | null;
  /** Display name of the currently active area filter, e.g. "Area A" or "All Areas". */
  selectedAreaName?: string;
  xAxisRange?: { min: number; max: number };
  onChartReady?: (instance: ECharts) => void;
}

interface SummaryCardProps {
  label: string;
  value: string;
  valueColor?: string;
}

function SummaryCard({ label, value, valueColor }: SummaryCardProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        bgcolor: "action.hover",
        minWidth: 80,
      }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "medium", color: valueColor }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

export function ThroughputTrendChart({
  lineTrendData,
  allLineTrendData,
  loading = false,
  error = null,
  lines,
  linesLoading = false,
  linesError = null,
  selectedLineKeys,
  onLineKeysChange,
  onLineHover,
  deploymentId,
  areas,
  hoveredLineKey = null,
  selectedAreaName,
  xAxisRange,
  onChartReady,
}: ThroughputTrendChartProps) {
  const theme = useTheme();

  const { chartData, series } = useMemo(
    () =>
      mapLineTrendDataToChartSeries(lineTrendData, {
        in: theme.palette.success.main,
        out: theme.palette.error.main,
      }),
    [lineTrendData, theme]
  );

  const chartConfig = useMemo<MultiLineChartConfig>(
    () => ({
      series,
      ariaDescription: "Throughput trend analytics chart showing visitor throughput over time",
      xAxisRange,
    }),
    [series, xAxisRange]
  );

  const aggregateTotals = useMemo(() => {
    let totalIn: number | null = null;
    let totalOut: number | null = null;
    for (const { points } of lineTrendData) {
      for (const point of points) {
        if (point.in != null) totalIn = (totalIn ?? 0) + point.in;
        if (point.out != null) totalOut = (totalOut ?? 0) + point.out;
      }
    }
    return {
      totalIn,
      totalOut,
      net: totalIn == null || totalOut == null ? null : totalIn - totalOut,
    };
  }, [lineTrendData]);

  const configErrorMessage = getThroughputTrendErrorMessage(linesError);
  const dataErrorMessage = getThroughputTrendErrorMessage(error);

  const showSummary = !linesLoading && configErrorMessage == null;
  function formatSummaryValue(value: number | null): string {
    return loading || value == null ? "–" : value.toLocaleString("en-GB");
  }

  return (
    <ChartCard
      title="Throughput Trend"
      icon={<ThroughputIcon sx={{ fontSize: "0.875rem" }} />}
      footerNote="Monitors the rate at which visitors navigate and exit transition points."
      contentSx={{ gap: 1 }}
    >
      {showSummary && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <SummaryCard
            label="Total In"
            value={formatSummaryValue(aggregateTotals.totalIn)}
            valueColor={theme.palette.success.main}
          />
          <SummaryCard
            label="Total Out"
            value={formatSummaryValue(aggregateTotals.totalOut)}
            valueColor={theme.palette.error.main}
          />
          <SummaryCard label="Net Flow" value={formatSummaryValue(aggregateTotals.net)} />
          {selectedAreaName != null && (
            <SummaryCard label="Selected Area" value={selectedAreaName} />
          )}
        </Box>
      )}

      {linesLoading ? (
        <ChartLoading label="Loading throughput line configurations" />
      ) : configErrorMessage != null ? (
        <ChartErrorState message={configErrorMessage} />
      ) : lines.length === 0 ? (
        <ChartEmptyState
          title="No throughput lines configured"
          description="Configure a throughput line for this area to view its trend."
        />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {/* Side line selection panel */}
          <Box
            sx={{
              width: { xs: "100%", md: 450 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: { xs: 320, md: "none" },
            }}
          >
            <ThroughputLineSelector
              lines={lines}
              lineTrendData={allLineTrendData ?? lineTrendData}
              selectedKeys={selectedLineKeys}
              onSelectionChange={onLineKeysChange}
              onLineHover={onLineHover}
              loading={linesLoading}
              disabled={configErrorMessage != null}
            />
          </Box>

          {/* Chart area */}
          <Box sx={{ flex: "1 1 0", minWidth: 0, minHeight: 240 }}>
            {loading ? (
              <ChartLoading label="Loading throughput trend data" />
            ) : dataErrorMessage != null ? (
              <ChartErrorState message={dataErrorMessage} />
            ) : selectedLineKeys.size === 0 ? (
              <ChartEmptyState
                title="No throughput line selected"
                description="Select a throughput line from the panel to view its trend."
              />
            ) : chartData.length === 0 ||
              chartData.every((point) =>
                Object.values(point.values).every((value) => value == null)
              ) ? (
              <ChartEmptyState
                title="No throughput trend data"
                description="Throughput trend data will appear once observations are available."
              />
            ) : (
              <MultiLineChart
                data={chartData}
                config={chartConfig}
                height={240}
                onChartReady={onChartReady}
              />
            )}
          </Box>
        </Box>
      )}

      {deploymentId != null && areas != null && (
        <ThroughputLineMap
          deploymentId={deploymentId}
          areas={areas}
          lines={lines}
          selectedLineKeys={selectedLineKeys}
          hoveredLineKey={hoveredLineKey}
        />
      )}
    </ChartCard>
  );
}
